import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import { formatApproxEur } from '../utils/currency';
import { Reservation } from '../types';
import { CONTACT } from '../config';

// Převod českého čísla účtu na IBAN
function accountToIBAN(accountNumber: string): string | null {
  const parts = accountNumber.replace(/\s/g, '').split('/');
  if (parts.length !== 2) return null;

  let [account, bankCode] = parts;
  let prefix = '000000';

  if (account.includes('-')) {
    const split = account.split('-');
    prefix = split[0].padStart(6, '0');
    account = split[1];
  }

  bankCode = bankCode.padStart(4, '0');
  account = account.padStart(10, '0');

  // BBAN = kód banky + předčíslí + číslo účtu
  const bban = bankCode + prefix + account;

  // Výpočet kontrolního čísla pro IBAN (C=12, Z=35)
  const numericString = bban + '123500';

  let remainder = 0;
  for (let i = 0; i < numericString.length; i++) {
    remainder = (remainder * 10 + parseInt(numericString[i], 10)) % 97;
  }

  const checkDigits = String(98 - remainder).padStart(2, '0');
  return 'CZ' + checkDigits + bban;
}

// Generování SPAYD řetězce pro QR platbu
function generateSPAYD(iban: string, amount: number, vs: string | number, message?: string): string {
  let spayd = 'SPD*1.0';
  spayd += '*ACC:' + iban;
  spayd += '*AM:' + amount.toFixed(2);
  spayd += '*CC:CZK';

  if (vs) {
    spayd += '*X-VS:' + vs;
  }

  if (message) {
    // Odstranit diakritiku a speciální znaky
    const cleanMessage = message
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .substring(0, 60);
    spayd += '*MSG:' + cleanMessage;
  }

  return spayd;
}

const TransferPayment: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const state = location.state as { reservation: Reservation } | null;
  const [reservation, setReservation] = useState<Reservation | null>(state?.reservation ?? null);
  const [error, setError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');

  useEffect(() => {
    if (state?.reservation) {
      setReservation(state.reservation);
      localStorage.setItem('reservationData', JSON.stringify(state.reservation));
      return;
    }
    const raw = localStorage.getItem('reservationData');
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Reservation;
        setReservation(parsed);
        return;
      } catch {
        // fall through to error
      }
    }
    setError(t('transfer.notFound'));
  }, [state, t]);

  useEffect(() => {
    if (reservation && !reservation.isVerified) {
      void navigate('/verify', { state: { reservation, paymentMethod: reservation.paymentMethod ?? null } });
    }
  }, [reservation, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Generování QR kódu lokálně pomocí knihovny qrcode
  useEffect(() => {
    const generateQR = async () => {
      if (!reservation || !reservation.totalPrice || !reservation.id) {
        return;
      }

      const iban = accountToIBAN(CONTACT.bankAccount);
      if (!iban) return;

      const spayd = generateSPAYD(iban, reservation.totalPrice, reservation.id, 'Chalupa Laura');
      
      try {
        const dataUrl = await QRCode.toDataURL(spayd, {
          width: 300,
          margin: 2,
          errorCorrectionLevel: 'M'
        });
        setQrDataUrl(dataUrl);
      } catch (err) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('Failed to generate QR code:', err);
        }
      }
    };

    generateQR();
  }, [reservation]);

  if (error) {
    return <p className="text-red-500 text-center mt-10">{error}</p>;
  }

  if (!reservation) {
    return <p className="text-center mt-10">{t('payment.loading')}</p>;
  }

  return (
    <div className="max-w-xl mx-auto mt-10 p-6 bg-white rounded shadow space-y-4">
      <h2 className="text-2xl font-bold text-center">{t('transfer.title')}</h2>
      <p className="text-center">{t('transfer.instructions')}</p>
      <div className="bg-gray-100 p-4 rounded space-y-1">
        <div><strong>{t('transfer.account')}:</strong> {CONTACT.bankAccount}</div>
        <div>
          <strong>{t('transfer.amount')}:</strong> {reservation.totalPrice?.toLocaleString()} Kč
          {i18n.language !== 'cs' && reservation.totalPrice && (
            <div className="text-sm text-gray-600 mt-1">(~ {formatApproxEur(reservation.totalPrice, i18n.language)})</div>
          )}
        </div>
        <div><strong>{t('transfer.variable')}:</strong> {reservation.id}</div>
        <div><strong>{t('transfer.reservationNumber')}:</strong> {reservation.id}</div>
      </div>
      <div className="flex justify-center">
        {qrDataUrl && <img alt="QR platba" src={qrDataUrl} className="w-[300px] h-[300px]" />}
      </div>
      <p className="text-center">
        {t('transfer.scanQr')}
      </p>
    </div>
  );
};

export default TransferPayment;
